import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Bot, MessageSquare, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/editorial";
import { useCookingChat, type ChatMsg } from "@/hooks/useCookingChat";
import { useEntitlements } from "@/features/paywall/useEntitlements";
import { PaywallSheet } from "@/features/paywall/PaywallSheet";
import type { CookingStep, Recipe } from "@/types/menu";

/**
 * Slide-up chat sheet for the cooking assistant (Phase 11).
 *
 * Premium-gated: free users see the PaywallSheet fallback.
 */
export function CookingChatSheet({
  open,
  onOpenChange,
  recipe,
  currentStep,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipe: Recipe;
  currentStep?: CookingStep | null;
}) {
  const { isPremium } = useEntitlements();
  const [showPaywall, setShowPaywall] = useState(false);
  const { messages, isStreaming, error, send, reset } = useCookingChat();
  const [input, setInput] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // Reset chat when reopening sheet
  useEffect(() => {
    if (open) {
      reset();
      // Focus input after sheet animates in
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open, reset]);

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    if (!isPremium) {
      setShowPaywall(true);
      return;
    }

    setInput("");
    void send({ recipe, currentStep, message: trimmed });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Handle premium_required error by showing paywall
  useEffect(() => {
    if (error === "premium_required") {
      setShowPaywall(true);
    }
  }, [error]);

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
          <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[70vh] flex-col rounded-t-2xl border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-hair px-5 py-4">
              <Eyebrow accent>
                <Bot className="size-3" /> Assistant Cuisine
              </Eyebrow>
              <Dialog.Close
                aria-label="Fermer"
                className="text-foreground/60 hover:text-foreground"
              >
                <X className="size-4" />
              </Dialog.Close>
            </div>

            {/* Message list */}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
            >
              {messages.length === 0 && !isStreaming && (
                <div className="flex flex-col items-center justify-center py-10 text-center text-foreground/50">
                  <MessageSquare className="mb-3 size-8" strokeWidth={1} />
                  <p className="font-serif text-[15px] italic">
                    Posez une question sur la recette en cours…
                  </p>
                  <p className="mt-1 text-[12px]">
                    Ingrédients, technique, alternatives…
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <ChatBubble key={i} msg={msg} />
              ))}

              {isStreaming && messages[messages.length - 1]?.role === "user" && (
                <div className="flex items-center gap-2 self-start text-foreground/50">
                  <span className="inline-block size-1.5 animate-bounce rounded-full bg-accent-dim" />
                  <span className="inline-block size-1.5 animate-bounce rounded-full bg-accent-dim [animation-delay:0.15s]" />
                  <span className="inline-block size-1.5 animate-bounce rounded-full bg-accent-dim [animation-delay:0.3s]" />
                </div>
              )}

              {error && error !== "premium_required" && (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-[13px] text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="flex items-center gap-2 border-t border-hair px-5 py-3">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Posez votre question…"
                disabled={isStreaming}
                className="flex-1 rounded-xl bg-foreground/5 px-4 py-2.5 text-[14px] text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-accent-dim disabled:opacity-50"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
                className="shrink-0"
                aria-label="Envoyer"
              >
                <Send className="size-4" />
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Paywall fallback */}
      <PaywallSheet
        open={showPaywall}
        onOpenChange={setShowPaywall}
        feature="Assistant Cuisine IA — posez vos questions en direct pendant la préparation."
      />
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function ChatBubble({ msg }: { msg: ChatMsg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed ${
          isUser
            ? "bg-accent-dim text-white"
            : "bg-foreground/5 text-foreground"
        }`}
      >
        {msg.content || (
          <span className="italic text-foreground/40">Réflexion…</span>
        )}
      </div>
    </div>
  );
}
